/*!
 * recording-latency.js — the ONE owner of "how late does a sung note land on
 * the timeline" for every tool that records a microphone while it plays
 * something the singer listens to (/vocal overdub and import, /recording-studio
 * backing track, /karaoke mix).
 *
 * The physics. A note the singer hears at graph time T was scheduled at T minus
 * the output latency; the singer answers; the answer reaches the graph at
 * T + input latency. So every take lands LATE by (output + input) latency —
 * 60–230 ms on real users' machines (studio calibrations, 90 days, both shards),
 * 125–136 ms on the laptop this was written on — and the browser's own numbers
 * (`ctx.outputLatency` + `track.getSettings().latency`) sum to about HALF of
 * that (57 ms here). A guess is not a measurement; only a loopback is.
 *
 * What it owns
 *   RecordingLatency.detect(pcm, sr, bursts, opts)   pure math, node-testable
 *   RecordingLatency.measure(opts)   → Promise<{ms, kind:'measured', …}>
 *   RecordingLatency.estimate(ctx, micStream) → {ms, kind:'estimate', …}
 *   RecordingLatency.deviceKey(ctx, micStream)
 *   RecordingLatency.recall(ctx, micStream) / remember(ctx, micStream, entry)
 *   RecordingLatency.resolve(ctx, micStream) → what a tool should apply NOW
 *   RecordingLatency.alignTake(ctx, buffer, startTime, latencyS) → {buffer, startTime, trimmedS}
 *   RecordingLatency.alignChannels(channels, sr, startTime, latencyS) → same, on Float32Arrays
 *   RecordingLatency.capture(ctx, micStream) → Promise<{stop() → {pcm, firstFrame, …}}>
 *   RecordingLatency.bindRow(rowEl, opts)    the shared <x-recording-sync> row
 *
 * ⚠️ The row's thumb is INERT until the first refresh with a running context,
 * and its `min` is the physical floor (`floorMs` = the browser's own output +
 * input buffers): a manual or migrated value below the floor is lifted to it
 * (`lifted:true`, `rec_latency_applied`). Production, first two hours after the
 * row shipped: 5 of 8 /vocal sessions recorded with «manual 0 ms» — the thumb
 * showed «0 ms · оценка» before any microphone existed, one nudge persisted 0
 * for that device pair for good. Only trusted gestures count (`isTrusted`),
 * and only `change` persists — `input` while dragging is preview.
 *
 * ⚠️ Pulling a take earlier than the timeline's 0 is NOT a clamp. A take that
 * starts at position 0 (the common case: count-in, then the backing plays
 * from the top) still arrives `latency` late; `Math.max(0, start − latency)`
 * places it at 0 unshifted, and the compensation silently does nothing for
 * exactly the takes people record most. alignTake() drops the head of the
 * take instead — the first `latency` seconds are what the microphone heard
 * BEFORE the singer could have answered anything.
 *
 * The detector. Four short Hann-windowed sine bursts at different frequencies
 * are played into the output and the microphone is captured through an
 * AudioWorklet tap that stamps every block with `currentFrame`. A SECOND tap
 * of the same kind sits on the burst generator itself, so each burst's true
 * start is read from the graph in the same frame domain — the number never
 * depends on how the main thread's `currentTime` maps onto the worklet's
 * `currentFrame` (measured to drift by whole render quanta between runs).
 * Detection is frequency-selective (Goertzel power at the burst frequency),
 * noise-calibrated (threshold = median floor at that frequency +12 dB, measured
 * with the same hop the detector uses) and then refined by a matched-length
 * sliding window whose peak sits at the true onset to within a hop of 32
 * samples (< 1 ms) — a matched filter on the envelope, immune to the
 * half-period ambiguity a waveform correlation has.
 *
 * Bench (tests/playwright/recording-latency-measures-the-loop.mjs): an
 * in-graph DelayNode loop with exact known delays, loud and −30 dB echo, with
 * and without broadband noise, 0–420 ms. The click-threshold detector this
 * replaces failed every −30 dB case and everything past 350 ms (that is the
 * 65 % failure rate the studio's «Калибровка» had in production); the
 * rAF-polled Goertzel probe hears everything but is ±10 ms.
 *
 * Classic script (ES5-safe body), no imports; `module.exports` for node tests.
 */
(function (global) {
    'use strict';

    var STORE_KEY = 'timbrica_rec_latency';
    var LEGACY_STUDIO_KEY = 'timbrica_studio_latency';
    var MAX_MS = 800;            // Bluetooth output can exceed 300 ms; the row's range too
    var HOP = 256;               // coarse Goertzel hop (same for floor and detection)
    var FINE_HOP = 32;           // matched-window refinement step
    var THRESH_DB = 12;          // burst must sit +12 dB over the floor at its frequency

    // ─────────────────────────────────────────────────────────────────
    // Pure math
    // ─────────────────────────────────────────────────────────────────

    /** Goertzel amplitude^2 of `freq` over buf[from..to). Pure sine of
     *  amplitude A at the bin → ~A^2. */
    function goertzelA2(buf, from, to, sr, freq) {
        var n = to - from;
        if (n <= 8) return 0;
        var k = Math.round(n * freq / sr);
        if (k < 1) k = 1;
        var w = 2 * Math.PI * k / n;
        var coeff = 2 * Math.cos(w);
        var s1 = 0, s2 = 0, s0, i;
        for (i = from; i < to; i++) {
            s0 = buf[i] + coeff * s1 - s2;
            s2 = s1; s1 = s0;
        }
        var p = s1 * s1 + s2 * s2 - coeff * s1 * s2;
        return p / (n * n / 4);
    }

    function median(arr) {
        if (!arr.length) return -1;
        var s = arr.slice().sort(function (a, b) { return a - b; });
        var m = s.length >> 1;
        return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    }

    /** The burst waveform — the SAME shape measure() plays, so the detector's
     *  matched window and the sound agree. Hann-windowed sine. */
    function burstTemplate(sr, freq, lenSamples) {
        var t = new Float32Array(lenSamples);
        for (var i = 0; i < lenSamples; i++) {
            var w = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (lenSamples - 1));
            t[i] = w * Math.sin(2 * Math.PI * freq * i / sr);
        }
        return t;
    }

    /**
     * Locate one burst.
     * @param {Float32Array} pcm      mono capture
     * @param {number} sr
     * @param {{at:number, freq:number, len:number}} b   at = sample index of the
     *        burst's scheduled start in `pcm` (zero-latency position), len = burst
     *        length in samples
     * @param {number} maxLag         search window in samples
     * @returns {{lag:number, snrDb:number}|null}
     */
    function locateBurst(pcm, sr, b, maxLag) {
        var from = Math.max(0, b.at | 0);
        var to = Math.min(pcm.length, from + maxLag + b.len);
        if (to - from < b.len + HOP * 4) return null;

        // 1. floor at this frequency: median hop power over the whole window
        //    (the burst occupies a few hops of it, the median ignores them).
        var hopA2 = [], i, hops = Math.floor((to - from) / HOP);
        for (i = 0; i < hops; i++) hopA2.push(goertzelA2(pcm, from + i * HOP, from + (i + 1) * HOP, sr, b.freq));
        var floor = Math.max(median(hopA2), 1e-12);
        var thr = Math.max(floor * Math.pow(10, THRESH_DB / 10), 2.5e-7);

        // 2. coarse onset: first run of two consecutive hot hops.
        var run = 0, coarseHop = -1;
        for (i = 0; i < hops; i++) {
            if (hopA2[i] > thr) { run++; if (run >= 2) { coarseHop = i - 1; break; } }
            else run = 0;
        }
        if (coarseHop < 0) return null;
        var peakA2 = 0;
        for (i = coarseHop; i < Math.min(hops, coarseHop + Math.ceil(b.len / HOP) + 1); i++) if (hopA2[i] > peakA2) peakA2 = hopA2[i];

        // 3. fine onset: slide a burst-length window from two hops before the
        //    coarse onset to two hops after; its power peaks when aligned.
        var lo = Math.max(from, from + coarseHop * HOP - 2 * HOP);
        var hi = Math.min(to - b.len, from + coarseHop * HOP + 2 * HOP);
        var best = -1, bestA2 = -1, pos, prevA2 = -1, nextA2 = -1, a2;
        var vals = [];
        for (pos = lo; pos <= hi; pos += FINE_HOP) {
            a2 = goertzelA2(pcm, pos, pos + b.len, sr, b.freq);
            vals.push(a2);
            if (a2 > bestA2) { bestA2 = a2; best = pos; }
        }
        if (best < 0) return null;
        // parabolic interpolation on the peak → sub-hop onset
        var bi = Math.round((best - lo) / FINE_HOP);
        if (bi > 0 && bi < vals.length - 1) {
            prevA2 = vals[bi - 1]; nextA2 = vals[bi + 1];
            var den = prevA2 - 2 * bestA2 + nextA2;
            if (den < 0) best += FINE_HOP * 0.5 * (prevA2 - nextA2) / den;
        }
        var lag = best - b.at;
        if (lag < -0.02 * sr || lag > maxLag) return null;
        return { lag: lag, snrDb: 10 * Math.log10(peakA2 / floor) };
    }

    /**
     * Detect the round-trip delay from a capture that contains several bursts.
     * @param {Float32Array} pcm    mono capture, sample 0 = capture start
     * @param {number} sr
     * @param {Array<{at:number,freq:number,len:number}>} bursts
     * @param {{maxLagMs?:number}} [opts]
     * @returns {{ms:number, samples:number[], hits:number, snrDb:number}|null}
     */
    function detect(pcm, sr, bursts, opts) {
        if (!pcm || !pcm.length || !bursts || !bursts.length) return null;
        var maxLag = Math.round(((opts && opts.maxLagMs) || MAX_MS) / 1000 * sr);
        var lags = [], snrs = [];
        for (var k = 0; k < bursts.length; k++) {
            var r = locateBurst(pcm, sr, bursts[k], maxLag);
            if (r) { lags.push(r.lag); snrs.push(r.snrDb); }
        }
        if (lags.length < 2) return null;
        var med = median(lags);
        var tol = 0.01 * sr;                                  // agree within 10 ms
        var good = [];
        for (var j = 0; j < lags.length; j++) if (Math.abs(lags[j] - med) <= tol) good.push(lags[j]);
        if (good.length < 2) return null;
        var avg = 0;
        for (var q = 0; q < good.length; q++) avg += good[q];
        avg /= good.length;
        return {
            ms: Math.round(avg / sr * 1000 * 10) / 10,
            samples: lags.map(function (l) { return Math.round(l / sr * 1000 * 10) / 10; }),
            hits: good.length,
            snrDb: Math.round(median(snrs))
        };
    }

    // ─────────────────────────────────────────────────────────────────
    // Browser glue
    // ─────────────────────────────────────────────────────────────────

    var TAP_NAME = 'timbrica-latency-tap';
    var TAP_SRC =
        'class T extends AudioWorkletProcessor{constructor(){super();this.on=true;this.port.onmessage=e=>{if(e.data&&e.data.cmd==="stop")this.on=false}}' +
        'process(i){if(!this.on)return false;var x=i[0];if(x&&x[0]){var n=x[0].length,m=new Float32Array(n);m.set(x[0]);if(x[1]){for(var j=0;j<n;j++)m[j]=(m[j]+x[1][j])*0.5}' +
        'this.port.postMessage({f:currentFrame,d:m},[m.buffer])}return true}}registerProcessor("' + TAP_NAME + '",T);';

    function ensureTap(ctx) {
        if (ctx.__rlTap) return ctx.__rlTap;
        if (!ctx.audioWorklet || typeof ctx.audioWorklet.addModule !== 'function') {
            ctx.__rlTap = Promise.resolve(false);
            return ctx.__rlTap;
        }
        var url = URL.createObjectURL(new Blob([TAP_SRC], { type: 'application/javascript' }));
        ctx.__rlTap = ctx.audioWorklet.addModule(url).then(function () { return true; }, function () { return false; });
        return ctx.__rlTap;
    }

    function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

    /**
     * Frame-stamped capture of a MediaStream. resolves to
     * {pcm: Float32Array, firstFrame: number, precise: boolean, peak: number}
     * `stop()` ends it.
     */
    function startCapture(ctx, stream, useWorklet) {
        var src = ctx.createMediaStreamSource(stream);
        var chunks = [], firstFrame = -1, peak = 0, node, precise = !!useWorklet, sink = null;
        function eat(f, d) {
            if (firstFrame < 0) firstFrame = f;
            for (var i = 0; i < d.length; i++) { var a = d[i] < 0 ? -d[i] : d[i]; if (a > peak) peak = a; }
            chunks.push(d);
        }
        if (useWorklet) {
            node = new AudioWorkletNode(ctx, TAP_NAME, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
            node.port.onmessage = function (e) { eat(e.data.f, e.data.d); };
            src.connect(node);
            // A worklet with an unconnected output is still pulled in Chromium
            // and Firefox; Safari wants a downstream sink. Mute it.
            sink = ctx.createGain(); sink.gain.value = 0; node.connect(sink).connect(ctx.destination);
        } else {
            node = ctx.createScriptProcessor(1024, 1, 1);
            var startFrame = Math.round(ctx.currentTime * ctx.sampleRate);
            var got = 0;
            node.onaudioprocess = function (e) {
                var d = new Float32Array(e.inputBuffer.getChannelData(0));
                eat(startFrame + got, d); got += d.length;
            };
            src.connect(node); node.connect(ctx.destination);
        }
        return {
            stop: function () {
                try { if (node.port) node.port.postMessage({ cmd: 'stop' }); } catch (e) {}
                try { src.disconnect(); } catch (e) {}
                try { node.disconnect(); } catch (e) {}
                try { if (sink) sink.disconnect(); } catch (e) {}
                var total = 0, i;
                for (i = 0; i < chunks.length; i++) total += chunks[i].length;
                var pcm = new Float32Array(total), w = 0;
                for (i = 0; i < chunks.length; i++) { pcm.set(chunks[i], w); w += chunks[i].length; }
                return { pcm: pcm, firstFrame: firstFrame, precise: precise, peak: peak };
            }
        };
    }

    /**
     * Frame-stamped capture of a TAKE — the same tap measure() listens with.
     * Resolves to {stop()}; stop() returns {pcm (mono Float32Array),
     * firstFrame, precise, peak}. `firstFrame / ctx.sampleRate` is the graph
     * time of the first captured sample, so a tool places the take on its
     * timeline by the graph's clock, not by when a main-thread callback
     * happened to run. /music-sequencer records over its playing beat with it.
     */
    function capture(ctx, stream) {
        return ensureTap(ctx).then(function (haveTap) { return startCapture(ctx, stream, haveTap); });
    }

    /** Frame-stamped tap on an AudioNode (the burst bus) — the reference. */
    function startRefCapture(ctx, node) {
        var chunks = [], firstFrame = -1, tap, sink;
        tap = new AudioWorkletNode(ctx, TAP_NAME, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
        tap.port.onmessage = function (e) { if (firstFrame < 0) firstFrame = e.data.f; chunks.push(e.data.d); };
        node.connect(tap);
        sink = ctx.createGain(); sink.gain.value = 0; tap.connect(sink).connect(ctx.destination);
        return {
            stop: function () {
                try { tap.port.postMessage({ cmd: 'stop' }); } catch (e) {}
                try { node.disconnect(tap); } catch (e) {}
                try { tap.disconnect(); } catch (e) {}
                try { sink.disconnect(); } catch (e) {}
                var total = 0, i;
                for (i = 0; i < chunks.length; i++) total += chunks[i].length;
                var pcm = new Float32Array(total), w = 0;
                for (i = 0; i < chunks.length; i++) { pcm.set(chunks[i], w); w += chunks[i].length; }
                return { pcm: pcm, firstFrame: firstFrame };
            }
        };
    }

    /**
     * Play bursts through `out`, listen on `micStream`, return the measured
     * round trip.
     * @param {{ctx:AudioContext, micStream:MediaStream, out?:AudioNode,
     *          attempts?:number, level?:number, isCancelled?:Function,
     *          onProgress?:Function}} opts
     */
    function measure(opts) {
        var ctx = opts.ctx, stream = opts.micStream, out = opts.out || ctx.destination;
        var attempts = opts.attempts || 4;
        var level = typeof opts.level === 'number' ? opts.level : 0.5;
        var isCancelled = opts.isCancelled || function () { return false; };
        var onProgress = opts.onProgress || function () {};
        var sr = ctx.sampleRate;
        var burstLen = Math.round(0.06 * sr);           // 60 ms Hann burst
        var gapS = 1.0;                                  // room for 800 ms of lag + tail
        var t = null, cap = null, ref = null, haveTapG = false;
        var resumed = ctx.state === 'running' ? Promise.resolve() : ctx.resume().catch(function () {});
        return resumed.then(function () { return ensureTap(ctx); }).then(function (haveTap) {
            haveTapG = haveTap;
            cap = startCapture(ctx, stream, haveTap);
            return wait(350);                             // floor + let the stream prime
        }).then(function () {
            var t0 = ctx.currentTime + 0.25;
            var bursts = [];
            var bus = ctx.createGain(); bus.gain.value = 1;   // every burst passes here → reference tap
            bus.connect(out);
            ref = haveTapG ? startRefCapture(ctx, bus) : null;
            for (var k = 0; k < attempts; k++) {
                var freq = 1500 + k * 400;               // 1500/1900/2300/2700 Hz — laptop speaker/mic friendly
                var at = t0 + k * gapS;
                var buf = ctx.createBuffer(1, burstLen, sr);
                buf.getChannelData(0).set(burstTemplate(sr, freq, burstLen));
                var src = ctx.createBufferSource(); src.buffer = buf;
                var g = ctx.createGain(); g.gain.value = level;
                src.connect(g).connect(bus);
                src.start(at);
                bursts.push({ atTime: at, freq: freq, len: burstLen });
            }
            t = bursts;
            var end = t0 + (attempts - 1) * gapS + (MAX_MS / 1000) + 0.25;
            return (function tick() {
                if (isCancelled()) return Promise.resolve();
                if (ctx.currentTime >= end) return Promise.resolve();
                return wait(100).then(tick);
            })();
        }).then(function () {
            var c = cap.stop();
            var rc = ref ? ref.stop() : null;
            if (isCancelled()) return { ms: -1, kind: 'measured', samples: [], failure: 'cancelled', diag: {} };
            var f0 = c.firstFrame < 0 ? Math.round(ctx.currentTime * sr) : c.firstFrame;
            var bursts = t.map(function (b) { return { at: Math.round(b.atTime * sr) - f0, freq: b.freq, len: b.len }; });
            var refOk = false;
            if (rc && rc.firstFrame >= 0) {
                // Read each burst's TRUE start from the output path: same frame
                // domain as the microphone tap, no main-thread clock involved.
                // The nominal start may sit AFTER the real one by whole render
                // quanta (main-thread clock drift), so search from 50 ms before it.
                var pre = Math.round(0.05 * sr);
                var refBursts = t.map(function (b) { return { at: Math.round(b.atTime * sr) - rc.firstFrame - pre, freq: b.freq, len: b.len }; });
                var located = 0;
                for (var q = 0; q < refBursts.length; q++) {
                    var lb = locateBurst(rc.pcm, sr, refBursts[q], Math.round(0.15 * sr));
                    if (lb) { bursts[q].at = Math.round(refBursts[q].at + lb.lag + rc.firstFrame - f0); located++; }
                }
                refOk = located === refBursts.length;
            }
            var r = detect(c.pcm, sr, bursts, { maxLagMs: MAX_MS });
            var failure = '';
            // «Микрофон молчит» vs «сигналы не дошли» — the line is
            // latency-probe.js's (recording-sync loads it first). A singer in
            // headphones in a quiet room peaks far under the old 0.008 and was
            // told to pick another mic. Without the owner, the claim that
            // does not blame a working mic.
            var LP = global.LatencyProbe;
            if (!r) failure = LP && LP.micFailure ? LP.micFailure(c.peak) : 'not_heard';
            var diag = {
                micPeak: Math.round(c.peak * 1000) / 1000,
                micPeakDb: LP && LP.peakDb ? LP.peakDb(c.peak) : null,
                precise: c.precise && refOk,
                outLatMs: Math.round((ctx.outputLatency || 0) * 1000),
                baseLatMs: Math.round((ctx.baseLatency || 0) * 10000) / 10,
                trackLatMs: trackLatencyMs(stream),
                snrDb: r ? r.snrDb : null,
                hits: r ? r.hits : 0
            };
            for (var k = 0; k < bursts.length; k++) onProgress(k, r && r.samples[k] != null ? r.samples[k] : null);
            return { ms: r ? Math.round(r.ms) : -1, kind: 'measured', samples: r ? r.samples : [], failure: failure, diag: diag };
        });
    }

    function trackLatencyMs(stream) {
        try {
            var st = stream && stream.getAudioTracks && stream.getAudioTracks()[0] && stream.getAudioTracks()[0].getSettings();
            if (st && typeof st.latency === 'number') return Math.round(st.latency * 1000);
        } catch (e) {}
        return 0;
    }

    /** The browser's own guess. Honest label: 'estimate'. Measured to be
     *  ~half of the true loop on Windows/Realtek (57 vs 130 ms). */
    function estimate(ctx, micStream) {
        var out = 0;
        try { out = ((ctx.outputLatency || (ctx.baseLatency || 0) * 2) || 0) * 1000; } catch (e) {}
        var inp = trackLatencyMs(micStream);
        if (!inp) inp = 20;
        var ms = Math.max(0, Math.min(MAX_MS, Math.round(out + inp)));
        return { ms: ms, kind: 'estimate', outLatMs: Math.round(out), inLatMs: inp };
    }

    function sinkKey(ctx) {
        try {
            var s = ctx.sinkId;
            if (s && typeof s === 'object') return s.type || 'none';
            return s || 'default';
        } catch (e) { return 'default'; }
    }

    /** Persistence key: this input device × this output × this sample rate. */
    function deviceKey(ctx, micStream) {
        var inp = 'mic';
        try {
            var tr = micStream && micStream.getAudioTracks()[0];
            var st = tr && tr.getSettings();
            inp = (st && (st.deviceId || st.groupId)) || (tr && tr.label) || 'mic';
        } catch (e) {}
        return inp + '|' + sinkKey(ctx) + '|' + (ctx ? ctx.sampleRate : 0);
    }

    function loadStore() {
        try {
            var raw = localStorage.getItem(STORE_KEY);
            var s = raw ? JSON.parse(raw) : null;
            if (s && s.entries) return s;
        } catch (e) {}
        return { v: 1, entries: {} };
    }
    function saveStore(s) { try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) {} }

    /**
     * The physical floor: a sung note cannot reach the graph sooner than the
     * browser's own output buffer plus its input buffer, so any value below
     * that is not a setting — it is a mistake (a thumb nudged while the row
     * still showed «0 ms», a value migrated from the retired click detector).
     * Production, first two hours after the row shipped (22.09.2026): 5 of 8
     * /vocal sessions recorded with «manual 0» — nobody had chosen 0.
     */
    function floorMs(ctx, micStream) {
        var e = estimate(ctx, micStream);
        return e && e.ms > 0 ? e.ms : 0;
    }

    /** Remembered value for this device pair, the studio's pre-primitive
     *  scalar as a last resort (`legacy:true`), or null. A remembered
     *  measurement whose output latency no longer matches (the default
     *  output device changed — Bluetooth in, dock out) is returned with
     *  `stale:true`; resolve() will not apply it. A manual or migrated value
     *  below the physical floor comes back lifted to it (`lifted:true`). */
    function recall(ctx, micStream) {
        var s = loadStore(), key = deviceKey(ctx, micStream), e = s.entries[key];
        var floor = floorMs(ctx, micStream);
        if (e && typeof e.ms === 'number') {
            var outNow = Math.round(((ctx && ctx.outputLatency) || 0) * 1000);
            var stale = e.kind === 'measured' && typeof e.outLatMs === 'number' && outNow > 0 && Math.abs(outNow - e.outLatMs) > 15;
            var ms = e.ms, lifted = false;
            if (e.kind !== 'measured' && ms < floor) { ms = floor; lifted = true; }
            return { ms: ms, kind: e.kind, at: e.at, stale: stale, lifted: lifted, floor: floor, key: key };
        }
        try {
            var legacy = localStorage.getItem(LEGACY_STUDIO_KEY);
            if (legacy !== null && legacy !== '' && !isNaN(+legacy)) {
                var lm = Math.max(0, Math.min(MAX_MS, +legacy)), ll = false;
                if (lm < floor) { lm = floor; ll = true; }
                return { ms: lm, kind: 'manual', legacy: true, stale: false, lifted: ll, floor: floor, key: key };
            }
        } catch (e2) {}
        return null;
    }

    function remember(ctx, micStream, entry) {
        var s = loadStore(), key = deviceKey(ctx, micStream);
        s.entries[key] = {
            ms: Math.max(0, Math.min(MAX_MS, Math.round(entry.ms))),
            kind: entry.kind === 'measured' ? 'measured' : 'manual',
            at: Date.now(),
            outLatMs: Math.round(((ctx && ctx.outputLatency) || 0) * 1000)
        };
        saveStore(s);
        try { localStorage.setItem(LEGACY_STUDIO_KEY, String(s.entries[key].ms)); } catch (e) {}
        return s.entries[key];
    }

    function forget(ctx, micStream) {
        var s = loadStore(); delete s.entries[deviceKey(ctx, micStream)]; saveStore(s);
    }

    /** What to apply right now: a fresh remembered value, else the estimate. */
    function resolve(ctx, micStream) {
        var r = recall(ctx, micStream);
        if (r && !r.stale) return r;
        var e = estimate(ctx, micStream);
        e.floor = e.ms;
        if (r && r.stale) e.staleWas = r.ms;
        return e;
    }

    /**
     * Place a take on the timeline `latencyS` earlier than where recording
     * started. When that would be before 0, the head of the take is dropped.
     *
     * ⚠️ A take no longer than that head has NOTHING left: the answer is
     * `tooShort: true` with `buffer: null`, and the caller tells the person
     * instead of putting a clip on the timeline. Until 09.10.2026 the answer
     * was `Math.max(1, …)` — a one-sample buffer of silence, i.e. a clip nobody
     * recorded: /recording-studio telemetry has takes with peak −180 dB, and on
     * /vocal it was the selected clip that «Split» then cut into a zero-length
     * buffer (Sentry TIMBRICA-FRONTEND-NX).
     *
     * `trimmedFrames` is the dropped head in samples: a tool that keeps the RAW
     * take (the studio's file on disk) cuts the same head again on restore.
     * @param {AudioContext} ctx
     * @param {AudioBuffer} buffer
     * @param {number} startTime   timeline position where recording started (s)
     * @param {number} latencyS    loop + fixed processing delay (s)
     * @returns {{buffer:AudioBuffer|null, startTime:number, trimmedS:number, trimmedFrames:number, tooShort?:boolean}}
     */
    function alignTake(ctx, buffer, startTime, latencyS) {
        var shift = startTime - (latencyS || 0);
        if (shift >= 0 || !buffer) return { buffer: buffer, startTime: Math.max(0, shift), trimmedS: 0, trimmedFrames: 0 };
        var sr = buffer.sampleRate, drop = Math.min(buffer.length, Math.round(-shift * sr));
        if (drop >= buffer.length) return { buffer: null, startTime: 0, trimmedS: buffer.length / sr, trimmedFrames: buffer.length, tooShort: true };
        var len = buffer.length - drop;
        var out = ctx.createBuffer(buffer.numberOfChannels, len, sr);
        for (var c = 0; c < buffer.numberOfChannels; c++) out.getChannelData(c).set(buffer.getChannelData(c).subarray(drop, drop + len));
        return { buffer: out, startTime: 0, trimmedS: drop / sr, trimmedFrames: drop };
    }

    /** Same as alignTake on raw channel arrays (crash-restore paths), same
     *  `tooShort` answer (`channels: null`) when nothing of the take is left. */
    function alignChannels(channels, sr, startTime, latencyS) {
        var shift = startTime - (latencyS || 0);
        if (shift >= 0 || !channels || !channels.length) return { channels: channels, startTime: Math.max(0, shift), trimmedS: 0, trimmedFrames: 0 };
        var n = channels[0].length, drop = Math.min(n, Math.round(-shift * sr));
        if (drop >= n) return { channels: null, startTime: 0, trimmedS: n / sr, trimmedFrames: n, tooShort: true };
        return { channels: channels.map(function (ch) { return ch.subarray(drop); }), startTime: 0, trimmedS: drop / sr, trimmedFrames: drop };
    }

    // ─────────────────────────────────────────────────────────────────
    // The shared row (<x-recording-sync>): slider + value + kind + calibrate
    // ─────────────────────────────────────────────────────────────────

    /**
     * @param {Element} row   markup from components/recording-sync.blade.php
     * @param {{getCtx:Function, getMicStream:Function, acquireMic?:Function,
     *          releaseMic?:Function, onChange:Function, slug:string,
     *          isBusy?:Function}} opts
     *   getCtx()         → AudioContext (created if needed)
     *   getMicStream()   → live MediaStream or null
     *   acquireMic()     → Promise<MediaStream> when getMicStream() is null
     *   releaseMic(s)    → stop a stream acquireMic() opened
     *   onChange(ms, kind) → the tool stores what to apply
     * @returns {{set:Function, get:Function, apply:Function, refresh:Function}}
     */
    function bindRow(row, opts) {
        var range = row.querySelector('[data-rl-range]');
        var val = row.querySelector('[data-rl-val]');
        var kindEl = row.querySelector('[data-rl-kind]');
        var btn = row.querySelector('[data-rl-calibrate]');
        var note = row.querySelector('[data-rl-note]');
        var S = function (k) { return row.getAttribute('data-s-' + k) || ''; };
        var state = { ms: 0, kind: 'estimate', ready: false, floor: 0 };
        var busy = false;

        function render() {
            if (range) {
                /* Until a running context has answered what this device pair
                   is, the thumb is not a control: it showed «0 ms» and invited
                   a nudge that became «manual 0» for good. */
                range.disabled = !state.ready;
                if (state.ready) range.min = Math.max(0, Math.min(state.floor, state.ms));
                range.value = state.ms;
            }
            if (val) val.textContent = state.ready ? state.ms + ' ms' : '—';
            if (kindEl) {
                kindEl.textContent = S(state.kind === 'measured' ? 'measured' : state.kind === 'manual' ? 'manual' : 'estimate');
                kindEl.setAttribute('data-kind', state.kind);
                kindEl.hidden = !state.ready;
            }
            row.setAttribute('data-rl-state', state.ready ? state.kind : 'pending');
        }
        function say(text, tone) {
            if (!note) return;
            note.textContent = text || '';
            note.hidden = !text;
            note.setAttribute('data-tone', tone || '');
        }
        function set(ms, kind, persist) {
            state.ms = Math.max(state.ready ? Math.min(state.floor, MAX_MS) : 0, Math.min(MAX_MS, Math.round(ms)));
            state.kind = kind;
            state.ready = true;
            render();
            if (persist) {
                try { remember(opts.getCtx(), opts.getMicStream(), { ms: state.ms, kind: kind }); } catch (e) {}
            }
            try { opts.onChange(state.ms, state.kind); } catch (e) {}
        }
        /** Re-read the store for the CURRENT devices (call once the mic is open). */
        function refresh() {
            var ctx = null, stream = null;
            try { ctx = opts.getCtx(); stream = opts.getMicStream(); } catch (e) {}
            if (!ctx) return;
            var r = resolve(ctx, stream);
            state.ms = r.ms; state.kind = r.kind; state.floor = r.floor || 0; state.ready = true; render();
            if (r.kind === 'estimate') say(r.staleWas != null ? S('stale') : S('estimate_note'), 'warn');
            else say('', '');
            try { opts.onChange(state.ms, state.kind); } catch (e) {}
            try {
                if (global.__ae) global.__ae('feature_use', { slug: opts.slug, f: 'rec_latency_applied', ms: state.ms, kind: state.kind, legacy: r.legacy ? 1 : 0, lifted: r.lifted ? 1 : 0, stale: r.staleWas != null ? 1 : 0, floor: state.floor });
            } catch (e) {}
        }
        if (range) {
            /* A synthetic `input` (a settings binder, a page script syncing
               labels) is not a decision; only the person's own gesture is. */
            range.addEventListener('input', function (ev) {
                if (ev && ev.isTrusted === false) return;
                if (!state.ready) return;
                set(+this.value, 'manual', false); say('', '');
            });
            range.addEventListener('change', function (ev) {
                if (ev && ev.isTrusted === false) return;
                if (!state.ready) return;
                set(+this.value, 'manual', true);
                try { if (global.__ae) global.__ae('feature_use', { slug: opts.slug, f: 'rec_latency_set', ms: state.ms, floor: state.floor }); } catch (e) {}
            });
        }
        if (btn) btn.addEventListener('click', function () {
            if (busy || (opts.isBusy && opts.isBusy())) return;
            busy = true; btn.disabled = true; btn.classList.add('busy');
            var orig = btn.textContent; btn.textContent = S('calibrating');
            say(S('calibrating_note'), '');
            var ctx = null, stream = null, temp = null;
            Promise.resolve().then(function () {
                ctx = opts.getCtx();
                stream = opts.getMicStream();
                if (!stream && opts.acquireMic) return opts.acquireMic().then(function (s) { temp = s; stream = s; });
            }).then(function () {
                if (!stream) throw new Error('no-mic');
                return measure({ ctx: ctx, micStream: stream, attempts: 4, level: 0.5 });
            }).then(function (r) {
                var ae = global.__ae;
                if (r.ms >= 0) {
                    set(r.ms, 'measured', true);
                    say(S('done') + ': ' + r.ms + ' ms', 'ok');
                    try { if (ae) ae('feature_use', { slug: opts.slug, f: 'rec_latency_calibrate', ok: 1, ms: r.ms, precise: r.diag.precise ? 1 : 0, snr: r.diag.snrDb, hits: r.diag.hits, est: r.diag.outLatMs + (r.diag.trackLatMs || 20) }); } catch (e) {}
                } else {
                    say(S(r.failure === 'silent' ? 'failed_silent' : 'failed_not_heard'), 'warn');
                    try { if (ae) ae('feature_use', { slug: opts.slug, f: 'rec_latency_calibrate', ok: 0, fail: r.failure, micPk: r.diag.micPeak, micDb: r.diag.micPeakDb, precise: r.diag.precise ? 1 : 0 }); } catch (e) {}
                }
            }).catch(function (e) {
                say(S('failed_not_heard'), 'warn');
                try { if (global.__ae) global.__ae('feature_use', { slug: opts.slug, f: 'rec_latency_calibrate', ok: 0, fail: 'error', err: String(e && e.message || e).slice(0, 80) }); } catch (_) {}
            }).then(function () {
                if (temp && opts.releaseMic) { try { opts.releaseMic(temp); } catch (e) {} }
                busy = false; btn.disabled = false; btn.classList.remove('busy'); btn.textContent = orig;
            });
        });
        render();
        if (!state.ready) say(S('pending'), '');
        return { set: set, get: function () { return { ms: state.ms, kind: state.kind, ready: state.ready, floor: state.floor }; }, refresh: refresh, say: say };
    }

    var api = {
        detect: detect,
        measure: measure,
        estimate: estimate,
        deviceKey: deviceKey,
        recall: recall,
        remember: remember,
        forget: forget,
        floorMs: floorMs,
        resolve: resolve,
        alignTake: alignTake,
        alignChannels: alignChannels,
        capture: capture,
        bindRow: bindRow,
        burstTemplate: burstTemplate,
        MAX_MS: MAX_MS,
        _goertzelA2: goertzelA2
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    global.RecordingLatency = api;
})(typeof window !== 'undefined' ? window : globalThis);
